/**
 * Pack Preview Component
 * Shows pack information and recommendation details for selection
 * Used by PackSelectionInterface for displaying available packs
 */
import React from 'react';
import { PackRecommendationEngine } from '../utils/PackRecommendationEngine';
import './PackPreview.css';

const PackPreview = ({ pack, recommendation, selectable, onSelect, showRecommendation = false }) => {
    if (!pack) return null;

    const handleClick = () => {
        if (selectable && onSelect) {
            onSelect(pack);
        }
    };

    const urgencyColor = recommendation 
        ? PackRecommendationEngine.getUrgencyColor(recommendation.urgency_level)
        : '#95a5a6';
    
    const urgencyLabel = recommendation 
        ? PackRecommendationEngine.getUrgencyLabel(recommendation.urgency_level)
        : 'OPTION';

    const getRarityColor = (rarity) => {
        switch (rarity) {
            case 'LEGENDARY': return '#FFD700';
            case 'RARE': return '#9A4FFF';
            case 'SILVER': return '#C0C0C0';
            case 'BRONZE': return '#CD7F32';
            case 'COMMON': return '#8B8B8B';
            default: return '#3498db';
        }
    };

    return (
        <div 
            className={`pack-preview ${selectable ? 'selectable' : 'disabled'}`}
            onClick={handleClick}
            style={{
                cursor: selectable ? 'pointer' : 'not-allowed',
                opacity: selectable ? 1 : 0.6
            }}
        >
            {/* Pack Image/Icon */}
            <div className="pack-preview-image">
                <img
                    src={pack.background_image}
                    alt={pack.display_name || pack.name || 'Pack'}
                    onError={e => {
                        console.error(`❌ Failed to load pack background: ${pack.background_image} for rarity ${pack.rarity_tier}`);
                        // NO SILENT FALLBACKS - let it fail loudly so we can fix the issue
                    }}
                />
                
                {/* Card count overlay — backend-driven; hide rather than show a
                    wrong hardcoded count when card_count is missing */}
                {(pack.card_count !== undefined && pack.card_count !== null) && (
                    <div className="card-count-overlay">
                        {pack.card_count} cards
                    </div>
                )}
            </div>

            {/* Pack Details */}
            <div className="pack-preview-details">
                <h4 className="pack-name">
                    {pack.display_name || pack.name || 'Draft Pack'}
                </h4>
                
                {/* Rarity tier */}
                <div 
                    className="pack-rarity"
                    style={{
                        backgroundColor: getRarityColor(pack.rarity_tier),
                        color: pack.rarity_tier === 'LEGENDARY' ? '#333' : 'white'
                    }}
                >
                    {pack.rarity_tier || 'COMMON'}
                </div>

                {/* Expected contents */}
                {pack.expected_contents && (
                    <div className="expected-contents">
                        <div className="contents-title">Expected:</div>
                        <div className="contents-list">
                            {pack.expected_qb_probability > 0 && (
                                <span className="content-item">
                                    QB ({Math.round(pack.expected_qb_probability * 100)}%)
                                </span>
                            )}
                            {pack.expected_rb_probability > 0 && (
                                <span className="content-item">
                                    RB ({Math.round(pack.expected_rb_probability * 100)}%)
                                </span>
                            )}
                            {pack.expected_wr_probability > 0 && (
                                <span className="content-item">
                                    WR ({Math.round(pack.expected_wr_probability * 100)}%)
                                </span>
                            )}
                            {pack.expected_item_probability > 0 && (
                                <span className="content-item">
                                    Gear ({Math.round(pack.expected_item_probability * 100)}%)
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Guaranteed items */}
                {pack.pack_config && (
                    <div className="guaranteed-contents">
                        <div className="contents-title">Guaranteed:</div>
                        <div className="contents-list">
                            {pack.pack_config.guaranteed_qb && (
                                <span className="content-item guaranteed">QB ✓</span>
                            )}
                            {pack.pack_config.guaranteed_rb && (
                                <span className="content-item guaranteed">RB ✓</span>
                            )}
                            {pack.pack_config.guaranteed_wr && (
                                <span className="content-item guaranteed">WR ✓</span>
                            )}
                            {pack.pack_config.guaranteed_gear && (
                                <span className="content-item guaranteed">Gear ✓</span>
                            )}
                        </div>
                    </div>
                )}

                {/* Pack theme */}
                {pack.pack_config?.theme && (
                    <div className="pack-theme">
                        Theme: {pack.pack_config.theme}
                    </div>
                )}
            </div>

            {/* Recommendation section */}
            {showRecommendation && recommendation && (
                <div className="recommendation-section">
                    <div 
                        className="urgency-badge"
                        style={{ backgroundColor: urgencyColor }}
                    >
                        {urgencyLabel}
                    </div>
                    
                    <div className="recommendation-score">
                        Score: {recommendation.recommendation_score || 0}
                    </div>
                    
                    <div className="recommendation-reason">
                        {recommendation.recommendation_reason}
                    </div>
                </div>
            )}

            {/* Selection indicator */}
            {selectable && (
                <div className="selection-prompt">
                    Click to select
                </div>
            )}
        </div>
    );
};

export default PackPreview;